/**
 * Hash routing — WP7 spec forbids a router library. Two things:
 *
 *   `parseHashRoute(hash)` — pure parser, tested exhaustively.
 *   `useHashRoute()`       — React hook, subscribes to `hashchange`.
 *
 * Route shapes:
 *   ""      / "#"        / "#/"          → { kind: "home" }
 *   "#/join/<matchId>"                    → { kind: "join", matchId }
 *   "#/match"                             → { kind: "match" }
 *   "#/practice"                          → { kind: "practice" } (#499)
 *   "#/practice-dummy"                    → { kind: "dummyPractice" } (#609)
 *   anything else                         → { kind: "home" } (soft fallback,
 *                                           see WP7 spec — Home is the
 *                                           only screen we can always render)
 */

import { useEffect, useState } from "react";

export type Route =
  | { readonly kind: "home" }
  | { readonly kind: "join"; readonly matchId: string }
  | { readonly kind: "match" }
  | { readonly kind: "settings" }
  | { readonly kind: "demo" }
  | { readonly kind: "practice" }
  | { readonly kind: "dummyPractice" };

/**
 * Parse a `window.location.hash` value into a `Route`. Accepts values
 * with or without the leading `#`.
 */
export function parseHashRoute(hash: string): Route {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const clean = raw.startsWith("/") ? raw.slice(1) : raw;
  if (clean === "") return { kind: "home" };
  const segments = clean.split("/");
  const head = segments[0];
  if (head === "match" && segments.length === 1) {
    return { kind: "match" };
  }
  if (head === "demo" && segments.length === 1) {
    return { kind: "demo" };
  }
  if (head === "settings" && segments.length === 1) {
    return { kind: "settings" };
  }
  if (head === "practice" && segments.length === 1) {
    return { kind: "practice" };
  }
  if (head === "practice-dummy" && segments.length === 1) {
    return { kind: "dummyPractice" };
  }
  if (head === "join" && segments.length === 2) {
    const matchId = segments[1];
    if (matchId !== undefined && matchId.length > 0) {
      return { kind: "join", matchId };
    }
  }
  return { kind: "home" };
}

/**
 * Build a hash string from a route. Inverse of `parseHashRoute`.
 * Always starts with `#/` so the URL is normalized after every nav.
 */
export function routeToHash(route: Route): string {
  switch (route.kind) {
    case "home":
      return "#/";
    case "match":
      return "#/match";
    case "demo":
      return "#/demo";
    case "settings":
      return "#/settings";
    case "practice":
      return "#/practice";
    case "dummyPractice":
      return "#/practice-dummy";
    case "join":
      return `#/join/${route.matchId}`;
  }
}

/**
 * Navigate: mutates `window.location.hash`. Triggers `hashchange` for
 * every listener including `useHashRoute`.
 */
export function navigate(route: Route): void {
  if (typeof window === "undefined") return;
  const next = routeToHash(route);
  if (window.location.hash !== next) {
    window.location.hash = next;
  }
}

/**
 * React hook. Reads the current route + subscribes to hashchange. In
 * SSR / test setups without a window it returns `{ kind: "home" }`.
 */
export function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    typeof window === "undefined" ? { kind: "home" } : parseHashRoute(window.location.hash),
  );
  useEffect(() => {
    const onChange = (): void => {
      setRoute(parseHashRoute(window.location.hash));
    };
    window.addEventListener("hashchange", onChange);
    // React 18 StrictMode double-mounts effects; the second mount will
    // see the same hash and stay idempotent.
    onChange();
    return () => {
      window.removeEventListener("hashchange", onChange);
    };
  }, []);
  return route;
}
